import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors, ValidatorFn, FormGroup } from '@angular/forms';

import { jwtDecode } from "jwt-decode";
import { AuthService } from '../../../services/auth.service';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '../../../services/theme.service';
import { OpenModalConfirmService } from '../../../services/open-modal-confirm.service';
import { CountdownComponent } from "../../../components/double-authentication-countdown/double-authentication-countdown.component";

// import { ModalLoginSelectComponent } from '../../components/modal-login-select/modal-login-select.component';

@Component({
  selector: 'app-graos-do-para-login',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    ReactiveFormsModule,
    // ModalLoginSelectComponent,
    MatTooltipModule,
    CountdownComponent
],
  templateUrl: './graos-do-para-login.component.html',
  styleUrl: './graos-do-para-login.component.scss'
})
export class GraosDoParaLoginComponent implements OnInit, AfterViewInit {
  private titleService = inject(Title);
  private _snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private openModalConfirmService = inject(OpenModalConfirmService);

  redInput = false;
  isIncorrectEmail = false;
  isCorrectingPassword = false;
  isConfirmingEmail = false;
  isConfirmedPassword = false;
  isCodeSent = false;
  isPasswordsTheSame = true;
  newPasswordData: any = { username: '', oldPassword: '', newPassword: '' }
  doubleAuthentication = false;
  timeExpired = false;
  doubleAuthenticationCount = 3;

  doubleAuthenticationForm: FormGroup = this.fb.group({
    code: ['', Validators.required],
  });

  loginForm = this.fb.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required]]
  });

  emailForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]]
  })

  newPasswordForm = this.fb.group({
    newPassword: ['', [Validators.required, this.strongPasswordValidator()]],
    confirmNewPassword: ['', [Validators.required, this.strongPasswordValidator()]]
  })

  incorrectPassword = false;

  ngOnInit(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.themeService.setThemeByThemeName('GRAOSDOPARA');
  }

  ngAfterViewInit(): void {
    this.themeService.setThemeByThemeName('GRAOSDOPARA');
  }

  navigateToHome(): void {
    this.router.navigate(['/panel'])
  }

  redirectByToken () {
    const userItem = sessionStorage.getItem('user');
    const user = userItem ? JSON.parse(userItem) : {};
    if (user.authorities.some((item: string) => item === 'ROLE_GRAOSDOPARA_ADMIN')) {
      this.titleService.setTitle('Verde de Grãos do Pará');
      sessionStorage.setItem('systemType', 'GRAOSDOPARA');
      this.router.navigate(['/panel']);
      this._snackBar.open(this.translate.instant('LOGIN.SUCCESS'), undefined,
        {
          panelClass: ['alert_success'],
          horizontalPosition: 'right',
          verticalPosition: 'top',
        }
      );
    } else if (user.confirmPersonalData) {
      this.router.navigate(['/public-panel'], {
        queryParams: { userId: user.customerId },
      });
    } else if (user.authorities.some((item: string) => item === 'ROLE_GRAOSDOPARA_REQUESTER')) {
      this.titleService.setTitle('Verde de Grãos do Pará');
      sessionStorage.setItem('systemType', 'GRAOSDOPARA');
      this.router.navigate(['/client-panel']);
    } else {
      this.incorrectPassword = true;
      this._snackBar.open(
        this.translate.instant('LOGIN.ERROR'),
        undefined,
        {
          panelClass: ['alert_success'],
          horizontalPosition: 'right',
          verticalPosition: 'top',
        }
      );
    }
  }

  submitForm() {
    if (this.loginForm.valid) {
      this.authService.login(this.username.value, this.password.value).subscribe(
        (res) => {
          this.redirectByToken();
        },
       (error) => {
          const errorType = JSON.parse(error.error)
          if (errorType?.status === "TWO_FACTOR_REQUIRED") {
            if(this.timeExpired === true) {
              this.openModalConfirmService.openModalConfirm({
                text: 'Token expirado!.',
                subText: 'Um novo token de autenticação foi enviado para o e-mail do usuário.',
                type: 'warning',
                hideCancelButton: true,
              }).subscribe(() => {
                this.isCorrectingPassword = false;
                this.doubleAuthentication = true;
                this.timeExpired = false;
              });
              return;
            }
            this.openModalConfirmService.openModalConfirm({
              text: 'Um token de autenticação foi enviado para o e-mail do usuário.',
              subText: 'Digite o token recebido para prosseguir com o acesso ao sistema.',
              type: 'warning',
              hideCancelButton: true,
            }).subscribe(() => {
              this.isCorrectingPassword = false;
              this.doubleAuthentication = true;
            });
            return;
          }
          if (errorType?.status === 'CHANGE_PASSWORD') {
            this.isCorrectingPassword = true;
            this._snackBar.open(
              this.translate.instant('LOGIN.CHANGE_PASSWORD'),
              undefined,
              {
                panelClass: ['alert_error'],
                horizontalPosition: 'right',
                verticalPosition: 'top',
              }
            );
          } else {
            this._snackBar.open(
              this.translate.instant('LOGIN.ERROR'),
              undefined,
              {
                panelClass: ['alert_error'],
                horizontalPosition: 'right',
                verticalPosition: 'top',
              }
            );
          }
        }
      );
    } else {
      this.incorrectPassword = true;
    }
  }

  handleForgetPassword(): void {
    this.isConfirmingEmail = true;
  }

  submitEmailCode(): void {
    const frontUrl = window.location.href;
    if (this.emailForm.valid) {
      const email = this.emailForm.value;
      this.authService.sendEmailResetPassword(email.email!.trim(), frontUrl).subscribe((response: any) => {
        this.isCodeSent = true;
        this.isIncorrectEmail = false;
      }, (error: Error) => {
        this.isIncorrectEmail = true;
      })
    } else {
      this.isIncorrectEmail = true;
    }
  }

  submitNewPassword(): void {
    const newPasswordFormValue = this.newPasswordForm.value;
    const loginForm = this.loginForm.value;
    if (this.newPasswordForm.valid) {
      if (newPasswordFormValue.newPassword !== newPasswordFormValue.confirmNewPassword) {
        this.redInput = true;
        this._snackBar.open(
          this.translate.instant('As senhas fornecidas não coincidem.'),
          undefined,
          {
            panelClass: ['alert_error'],
            horizontalPosition: 'right',
            verticalPosition: 'top',
          }
        );
      } else {
        this.newPasswordData = { username: loginForm.username!.trim(), oldPassword: loginForm.password!.trim(), newPassword: newPasswordFormValue.confirmNewPassword!.trim() }
        this.authService.changePassword(this.newPasswordData).subscribe((response: any) => {
          this.isCorrectingPassword = false;
          this.isConfirmingEmail = false;
          this.isConfirmedPassword = true;
          this.loginForm.reset();
        }, (error: Error) => {
          this.redInput = true;
          this._snackBar.open(
            this.translate.instant('FORGOT-PASSWORD.ERROR'),
            undefined,
            {
              panelClass: ['alert_error'],
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
        })
      }
    } else {
      const newPasswordErrors = this.newPasswordForm.get('newPassword')?.errors;
      const confirmNewPasswordErrors = this.newPasswordForm.get('confirmNewPassword')?.errors;
      if (newPasswordErrors!['required'] || confirmNewPasswordErrors!['required']) {
        this.redInput = true;
        this._snackBar.open(
          this.translate.instant('Todos os campos precisam ser preenchidos.'),
          undefined,
          {
            panelClass: ['alert_error'],
            horizontalPosition: 'right',
            verticalPosition: 'top',
          }
        );
      } else if (newPasswordErrors!['strongPassword'] || confirmNewPasswordErrors!['strongPassword']) {
        this.redInput = true;
        this._snackBar.open(
          this.translate.instant('As senhas não atendem ao critério de segurança.'),
          undefined,
          {
            panelClass: ['alert_error'],
            horizontalPosition: 'right',
            verticalPosition: 'top',
          }
        );
      }
    }
  }

  handleInitialLoginPage(): void {
    this.newPasswordForm.get('newPassword')!.reset();
    this.newPasswordForm.get('confirmNewPassword')!.reset();
    this.emailForm.get('email')!.reset();
    this.isCodeSent = false;
    this.isIncorrectEmail = false;
    this.isCorrectingPassword = false;
    this.isConfirmingEmail = false;
    this.isConfirmedPassword = false;
  }

  get username() {
    return this.loginForm.get('username') as FormControl;
  }

  get password() {
    return this.loginForm.get('password') as FormControl;
  }

  strongPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]+/.test(value);
      const hasLowerCase = /[a-z]+/.test(value);
      const hasNumeric = /[0-9]+/.test(value);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]+/.test(value);
      const hasMinLength = value.length >= 8;

      const passwordValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecial && hasMinLength;

      return !passwordValid ? { strongPassword: true } : null;
    };
  }

  submitDoubleAuthentication() {
    const username = this.loginForm.get('username')?.value ?? '';
    const code = this.doubleAuthenticationForm.get('code')?.value;
    this.doubleAuthenticationForm.markAllAsTouched();

    if (this.doubleAuthenticationForm.valid) {
      this.authService.doubleAuthentication(username, code).subscribe({
        next: () => {
          this._snackBar.open(
            this.translate.instant('FORGOT-PASSWORD.SUCCESS'),
            undefined,
            {
              panelClass: ['alert_success'],
              horizontalPosition: 'right',
              verticalPosition: 'top',
            }
          );
          this.redirectByToken();
        },
        error: (error) => {
          this.doubleAuthenticationCount = this.doubleAuthenticationCount - 1;
          this.doubleAuthenticationForm.get('code')?.setValue('');

          if (this.doubleAuthenticationCount > 0) {
            this.openModalConfirmService
              .openModalConfirm({
                text: 'Código inválido.',
                subText: `Você ainda possui ${
                  this.doubleAuthenticationCount
                } tentativa${
                  this.doubleAuthenticationCount > 1 ? 's' : ''
                } restante${this.doubleAuthenticationCount > 1 ? 's' : ''}.`,
                type: 'error',
                hideCancelButton: true,
              })
              .subscribe(() => {});
          } else {
            this.openModalConfirmService
              .openModalConfirm({
                text: 'Número máximo de tentativas atingido.',
                subText:
                  'Por favor, tente novamente mais tarde ou entre em contato com a equipe de suporte para obter assistência.',
                type: 'warning',
                hideCancelButton: true,
              })
              .subscribe(() => {
                this.doubleAuthentication = false;
                this.doubleAuthenticationCount = 3;
              });
          }
        },
      });
    }
  }

  onCountdownFinished() {
    this.timeExpired = true;
    this.submitForm();
  }
}
